import { useState, FormEvent } from 'react';
import Card from '../base/Card';
import Button from '../base/Button';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    nome: '',
    empresa: '',
    email: '',
    telefone: '',
    tipoInvestigacao: '',
    descricao: '',
    lgpdConsent: false
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!formData.lgpdConsent) {
      alert('É necessário aceitar os termos de privacidade para continuar.');
      return;
    }

    if (formData.descricao.length > 500) {
      alert('A descrição deve ter no máximo 500 caracteres.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          nome: formData.nome,
          empresa: formData.empresa,
          email: formData.email,
          telefone: formData.telefone,
          tipoInvestigacao: formData.tipoInvestigacao,
          descricao: formData.descricao
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus('success');
        setFormData({
          nome: '',
          empresa: '',
          email: '',
          telefone: '',
          tipoInvestigacao: '',
          descricao: '',
          lgpdConsent: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erro ao enviar formulário:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  return (
    <section id="contato" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h3 className="text-4xl font-bold text-gray-900 mb-4">
            Solicite uma Consultoria Gratuita
          </h3>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Entre em contato para discutir seu caso. Primeira consultoria é gratuita 
            e totalmente confidencial.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Card padding="lg">
            <form id="contato-investigacao" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      id="nome"
                      name="nome"
                      required
                      value={formData.nome}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="empresa" className="block text-sm font-medium text-gray-700 mb-2">
                      Empresa
                    </label>
                    <input
                      type="text"
                      id="empresa"
                      name="empresa"
                      value={formData.empresa}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="Nome da empresa"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="seu@email.com"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefone *
                    </label>
                    <input
                      type="tel"
                      id="telefone"
                      name="telefone"
                      required
                      value={formData.telefone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="tipoInvestigacao" className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Investigação *
                  </label>
                  <div className="relative">
                    <select
                      id="tipoInvestigacao"
                      name="tipoInvestigacao"
                      required
                      value={formData.tipoInvestigacao}
                      onChange={handleChange}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm appearance-none"
                    >
                      <option value="">Selecione o tipo de investigação</option>
                      <option value="empresarial-defensiva">Investigação Empresarial Defensiva</option>
                      <option value="funcionarios">Investigação de Funcionários</option>
                      <option value="patrimonial">Investigação Patrimonial</option>
                      <option value="outro">Outro</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição do Caso *
                  </label>
                  <textarea
                    id="descricao"
                    name="descricao"
                    required
                    rows={4}
                    maxLength={500}
                    value={formData.descricao}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                    placeholder="Descreva brevemente seu caso e suas necessidades (máximo 500 caracteres)"
                  />
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.descricao.length}/500 caracteres
                  </div>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="lgpdConsent"
                    name="lgpdConsent"
                    required
                    checked={formData.lgpdConsent}
                    onChange={handleChange}
                    className="mt-1 mr-3 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <label htmlFor="lgpdConsent" className="text-sm text-gray-600">
                    Aceito que meus dados sejam utilizados para contato conforme a 
                    <strong> Lei Geral de Proteção de Dados (LGPD)</strong>. 
                    Seus dados serão tratados com total confidencialidade.
                  </label>
                </div>

                <Button 
                  type="submit" 
                  fullWidth 
                  size="lg" 
                  disabled={isSubmitting}
                  className={isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Solicitar Consultoria Gratuita
                    </>
                  )}
                </Button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-check-circle-line text-green-600 mr-2"></i>
                      <span className="text-green-800 text-sm">
                        Mensagem enviada com sucesso! Entraremos em contato em breve.
                      </span>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-600 mr-2"></i>
                      <span className="text-red-800 text-sm">
                        Erro ao enviar mensagem. Tente novamente ou entre em contato por telefone.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </Card>

          <div className="space-y-8">
            <Card padding="lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Informações de Contato
              </h4>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-phone-line text-teal-600"></i>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Telefone / WhatsApp</div>
                    <div className="text-gray-600">(11) 99370-6525</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-mail-line text-teal-600"></i>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">E-mail</div>
                    <div className="text-gray-600">contato@detetivepatrick.com.br</div>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-calendar-line text-teal-600"></i>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Horário de Atendimento</div>
                    <div className="text-gray-600">Segunda a Sábado, 08h às 20h</div>
                  </div>
                </div>
              </div>
            </Card>

            <Card padding="lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Por que escolher nossos serviços?
              </h4>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                  <span className="text-gray-600 text-sm">Primeira consultoria gratuita e confidencial</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                  <span className="text-gray-600 text-sm">Documentação admissível legalmente</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                  <span className="text-gray-600 text-sm">Sigilo profissional garantido</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                  <span className="text-gray-600 text-sm">Relatórios detalhados e imparciais</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-check-line text-teal-600 mr-2 mt-0.5"></i>
                  <span className="text-gray-600 text-sm">Atendimento personalizado</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
